/**
 * Streamer Header Component
 * Common header for streamer screens with tab navigation design
 * Responsive: hamburger menu on mobile
 * Note: Streamers always see Japanese UI (no language selection)
 */

import { useState } from 'react';

interface StreamerHeaderProps {
    onMyPageClick?: () => void;
    onStudioClick?: () => void;
    onLogout?: () => void;
    activeTab: 'mypage' | 'broadcast' | 'studio';
    statusLabel?: string;
    username?: string;
    isPublishing?: boolean; // 配信中はスタジオボタンをdisabledに
}

// Fixed Japanese labels for streamers (no i18n)
const labels = {
    myPage: 'マイページ',
    studio: 'スタジオ',
    logout: 'ログアウト',
    menu: 'メニュー',
};

export default function StreamerHeader({
    onMyPageClick,
    onStudioClick,
    onLogout,
    activeTab,
    statusLabel,
    username,
    isPublishing = false
}: StreamerHeaderProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { key: 'mypage', label: labels.myPage, onClick: onMyPageClick, disabled: false },
        { key: 'studio', label: labels.studio, onClick: onStudioClick, disabled: isPublishing },
    ];

    return (
        <div className="bg-white py-1.5 md:py-2 shadow-sm">
            <div className="container mx-auto px-2 md:px-4">
                <div className="flex items-center justify-between">
                    {/* Left: Title */}
                    <div className="text-lg md:text-xl font-bold text-pink-600">
                        Live Chat
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-2 lg:gap-4">
                        {navItems.map((item) => (
                            <button
                                key={item.key}
                                onClick={() => {
                                    if (item.disabled) return;
                                    item.onClick?.();
                                    setIsMobileMenuOpen(false);
                                }}
                                disabled={item.disabled}
                                className={`px-4 lg:px-8 py-2 lg:py-3 font-semibold text-sm transition-all ${
                                    item.disabled
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : activeTab === item.key
                                            ? 'bg-white text-pink-500 shadow-md'
                                            : 'bg-white text-gray-600 hover:bg-pink-50'
                                } rounded-md`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Desktop Right Side */}
                    <div className="hidden md:flex items-center gap-2 lg:gap-4">
                        {username && (
                            <div className="text-sm text-pink-900 font-semibold">
                                {username}
                            </div>
                        )}
                        <button
                            onClick={onLogout}
                            data-testid="logout-button"
                            className="px-4 lg:px-8 py-2 lg:py-3 bg-white text-gray-600 font-semibold text-sm hover:bg-pink-50 transition-all rounded-md"
                        >
                            {labels.logout}
                        </button>
                        {statusLabel && (
                            <div className="bg-white border border-pink-300 px-3 lg:px-4 py-2 rounded-md">
                                <span className="text-sm font-semibold text-pink-900">{statusLabel}</span>
                            </div>
                        )}
                    </div>

                    {/* Mobile: Status + Hamburger */}
                    <div className="flex md:hidden items-center gap-2">
                        {statusLabel && (
                            <div className="bg-white border border-pink-300 px-2 py-1 rounded-md">
                                <span className="text-xs font-semibold text-pink-900">{statusLabel}</span>
                            </div>
                        )}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 bg-white rounded-md shadow-sm border border-pink-300"
                            aria-label={labels.menu}
                        >
                            {isMobileMenuOpen ? (
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden mt-3 bg-white rounded-lg shadow-lg border border-pink-200 overflow-hidden animate-slide-in-up">
                        {/* Username */}
                        {username && (
                            <div className="px-4 py-3 bg-pink-50 border-b border-pink-200 flex items-center gap-2">
                                <svg width="20" height="20" fill="currentColor" className="text-pink-500" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-semibold text-pink-900">{username}</span>
                            </div>
                        )}

                        {/* Nav Items */}
                        {navItems.map((item) => (
                            <button
                                key={item.key}
                                onClick={() => {
                                    if (item.disabled) return;
                                    item.onClick?.();
                                    setIsMobileMenuOpen(false);
                                }}
                                disabled={item.disabled}
                                className={`w-full px-4 py-3 text-left font-semibold text-sm transition-all border-b border-pink-100 ${
                                    item.disabled
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : activeTab === item.key
                                            ? 'bg-pink-50 text-pink-500'
                                            : 'text-gray-700 hover:bg-pink-50'
                                }`}
                            >
                                {item.label}
                            </button>
                        ))}

                        {/* Logout */}
                        <button
                            onClick={() => {
                                onLogout?.();
                                setIsMobileMenuOpen(false);
                            }}
                            data-testid="logout-button-mobile"
                            className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-600 hover:bg-pink-50 transition-all"
                        >
                            {labels.logout}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
